/**
 * Pure recurring-stream arithmetic. Kept free of Firebase imports so
 * computation modules (Found, insights) can reuse it without initialising the
 * app. `recurringTransactionsService` re-exports these for existing callers.
 */
import type { RecurringTransaction, RecurringFrequency } from '../../types/transactions';

/** Normalise one charge to a monthly cost. */
export const calculateMonthlyAmount = (amount: number, frequency: RecurringFrequency): number => {
  switch (frequency) {
    case 'WEEKLY': return amount * (52 / 12);
    case 'BIWEEKLY': return amount * (26 / 12);
    case 'SEMI_MONTHLY': return amount * 2;
    case 'MONTHLY': return amount;
    case 'ANNUALLY': return amount / 12;
    default: return amount; // treat UNKNOWN as monthly
  }
};

/** Display amount (prefer lastAmount, fall back to averageAmount). */
export const getDisplayAmount = (tx: RecurringTransaction): number => {
  return tx.lastAmount || tx.averageAmount;
};

/** Total monthly recurring spend across the active streams. */
export const calculateMonthlyRecurringTotal = (transactions: RecurringTransaction[]): number => {
  return transactions
    .filter(t => t.isActive)
    .reduce((sum, t) => sum + calculateMonthlyAmount(getDisplayAmount(t), t.frequency), 0);
};

/** Human-friendly frequency label. */
export const formatFrequency = (frequency: RecurringFrequency): string => {
  switch (frequency) {
    case 'WEEKLY': return 'Weekly';
    case 'BIWEEKLY': return 'Bi-weekly';
    case 'SEMI_MONTHLY': return 'Twice/mo';
    case 'MONTHLY': return 'Monthly';
    case 'ANNUALLY': return 'Yearly';
    default: return 'Recurring';
  }
};
